import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { SafetyObservation } from 'src/app/models/safety-observation.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from 'src/app/shared/rbac.service';
import { ApiDataSource } from 'src/app/utils/api-data-source';

@Component({
  selector: 'app-safety-observations-view-files',
  templateUrl: './safety-observations-view-files.component.html',
  styleUrls: ['./safety-observations-view-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SafetyObservationsViewFilesComponent implements OnInit {
  @Input('safety_observation_id') safety_observation_id: number;
  @Input('safety_observation') safety_observation: SafetyObservation;

  dataSource: SafetyObservationsFilesDataSource = new SafetyObservationsFilesDataSource(
    this.app,
    this.api
  );

  listColumns: string[] = [
    // 'select',
    'id',
    'thumbnail',
    'name',
    'date_created',
    // 'date_created_UTC',
    'actions'
  ];

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canEditSafetyObservations: boolean = false;

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService, // Used in view.
    public rbac: RBACService
  ) {}

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canEditSafetyObservations = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS,
        RBACService.RBAC_A.A_EDIT
      ),
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS,
        RBACService.RBAC_A.A_EDIT
      )
    ]);

    this.dataSource.safety_observation_id = this.safety_observation_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap((sorter) => {
          this.dataSource.order_by = sorter['active'];
          this.dataSource.order = sorter['direction'];

          // Sort UTC dates based on their corresponding date column.
          if (sorter['active'] == 'date_created_UTC') {
            this.dataSource.order_by = 'date_created';
          }

          this.dataSource.getData();
        })
      )
      .subscribe();
  }

  /**
   * Notifies the user that the URL has been copied to their clipboard via a toast message.
   *
   * @return {void} Does not return any value.
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }

  /**
   * View the uploaded files.
   * @param url The URL to view.
   */
  onViewFile(url: string) {
    this.utils.openUrl(url);
  }
}

export class SafetyObservationsFilesDataSource extends ApiDataSource {
  // Default records ordering
  order_by = 'date_created';
  order = 'desc';

  safety_observation_id: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest(
      `v2/safety-observations/${this.safety_observation_id}/files`,
      resetOffset
    );
  }
}
